import { createReducer, on } from '@ngrx/store';
import { SearchFormType } from '../search-page-type.enum';
import { SearchTypeInfo } from '../search-param.model';
import * as actions from './search-page.actions';
import { SearchPageState, searchPageInitialState, SearchPageDiff } from './search-page.state';
import { clone, findIndex, includes, setWith, remove, cloneDeep } from 'lodash-es';
import { ToolbarSliderOptions } from '../../se-toolbar/se-buttons-toolbar/types';

export const searchPageReducer = createReducer(
    searchPageInitialState,
    on(actions.seSearch, (state, { formType, searchType }) =>
        setDiff(changeSearchType(state, formType, searchType), formType, [])
    ),
    on(actions.seSearchSafeExit, (state, { formType }) => safeExit(state, formType)),
    on(actions.seSearchTypeChange, (state, { formType, searchType }) => changeSearchType(state, formType, searchType)),
    on(actions.seSearchSelect, (state, { formType, items, rows }) => selectItems(state, formType, items, rows)),
    on(actions.seSearchSetDiff, (state, { formType, diff }) => setDiff(state, formType, diff)),
    on(actions.seSearchSetItems, (state, { formType, items }) => setItems(state, formType, items)),
    on(actions.seSearchUpdateItemsProps, (state, { formType, items, prop, value }) =>
        updateItemsProp(state, formType, items, prop, value)
    ),
    on(actions.seSearchToggleLeftArea, (state, { formType, hide }) => setLeftAreaVisibility(state, formType, hide)),
    on(actions.seSearchToggleRightArea, (state, { formType, hide }) => setRightAreaVisibility(state, formType, hide)),
    on(actions.seSearchUpdateItems, (state, { formType, items, idPropertyName }) =>
        updateItems(state, formType, items, idPropertyName)
    ),
    on(actions.seSearchRemoveItems, (state, { formType, idList, idPropertyName }) =>
        removeItems(state, formType, idList, idPropertyName)
    )
);

export const updateState = (path: string, value: any, state: any) => setWith(clone(state), path, value, clone);

function changeSearchType(
    state: SearchPageState,
    formType: SearchFormType,
    searchType?: SearchTypeInfo
): SearchPageState {
    if (!searchType) {
        return state;
    }

    return updateState(`${formType}.actualSearchType`, searchType, state);
}

function safeExit(state: SearchPageState, formType?: SearchFormType): SearchPageState {
    if (!formType) {
        return Object.keys(state).reduce((s: SearchPageState, formType: SearchFormType) => {
            return safeExit(s, formType);
        }, state);
    }

    return {
        ...state,
        [formType]: {
            // Mantieni gli item selezionati all'uscita dalla pagina.
            ...state[formType],
            items: [],
            diff: [],
        }
    };
}

function selectItems(state: SearchPageState, formType: SearchFormType, items: string[], _rows: any[]): SearchPageState {
    return {
        ...state,
        [formType]: {
            ...state[formType],
            selectedItems: items,
        },
    };
}

function setDiff(state: SearchPageState, formType: SearchFormType, diff: SearchPageDiff[]): SearchPageState {
    return updateState(`${formType}.diff`, diff ?? [], state);
}

function setItems(state: SearchPageState, formType: SearchFormType, items: any[]): SearchPageState {
    const itemIds = items.map((i) => i.id);
    const selectedItems = state[formType].selectedItems.filter((item) => itemIds.indexOf(item) != -1);
    return {
        ...state,
        [formType]: {
            ...state[formType],
            items,
            selectedItems,
        },
    };
}

function updateItemsProp(
    state: SearchPageState,
    formType: SearchFormType,
    itemsToUpdate: string[],
    prop: string,
    value: any
): SearchPageState {
    const items = state[formType].items.map((item) => {
        if (includes(itemsToUpdate, item.id)) {
            return setWith(clone(item), prop, value, clone);
        }

        return item;
    });

    return updateState(`${formType}.items`, items, state);
}

function updateItems(
    state: SearchPageState,
    formType: SearchFormType,
    items: any[],
    idPropertyName: string
): SearchPageState {
    const _items = state[formType].items.map((_item) => {
        let index = findIndex(
            items,
            (newItem) => {
                return newItem[idPropertyName] == _item[idPropertyName];
            },
            0
        );

        if (index > -1) {
            return items[index];
        }

        return _item;
    });

    return updateState(`${formType}.items`, _items, state);
}

function removeItems(
    state: SearchPageState,
    formType: SearchFormType,
    idList: any[],
    idPropertyName: string
): SearchPageState {
    let _items = cloneDeep(state[formType].items);

    remove(_items, (item) => {
        let index = findIndex(
            idList,
            (id) => {
                return id == item[idPropertyName];
            },
            0
        );

        return index >= 0;
    });

    return updateState(`${formType}.items`, _items, state);
}

function setLeftAreaVisibility(state: SearchPageState, formType: SearchFormType, hide: boolean): SearchPageState {
    let leftSlider: ToolbarSliderOptions = {
        ...state[formType].leftSlider,
        checked: !hide,
    };

    return {
        ...state,
        [formType]: {
            ...state[formType],
            leftSlider: leftSlider,
        },
    };
}

function setRightAreaVisibility(state: SearchPageState, formType: SearchFormType, hide: boolean): SearchPageState {
    let rightSlider: ToolbarSliderOptions = {
        ...state[formType].rightSlider,
        checked: hide,
    };

    return {
        ...state,
        [formType]: {
            ...state[formType],
            rightSlider: rightSlider,
        },
    };
}
